import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { API } from "aws-amplify";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsSearching,
  setListing,
  setSearchText,
  setSelected,
} from "../store/ducks/user";
import { HeadCell } from "../models/dataTable";
import {
  CreateUserInput,
  ModelSortDirection,
  UpdateUserInput,
  User,
} from "../models/GQL_API";
import useApp from "./useApp";
import useAccount from "./useAccount";
import { validatePhone } from "../helpers/utils";
import useTimeline from "./useTimeline";
import useGroup from "./useGroup";
import {
  getUser,
  listUsers,
  usersByEmail,
  usersByPhoneNumber,
  userSortedByOrders,
} from "../graphql/queries";
import { createUser, deleteUser, updateUser } from "../graphql/mutations";
import { ListingVariables, TransactionListingVariables } from "../models/app";
import useTier from "./useTier";
import useConcept from "./useConcept";
import * as XLSX from "xlsx";

const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { session, showConfirm, showError, setSelectedUser } = useApp();
  const { groupsSelectedFilters, groupsOptions, groupsGetName } = useGroup(
    "groups",
    "group"
  );
  const { tiersGetName, tiersSelectedFilters } = useTier("tiers", "tier");
  const { accountsSelected } = useAccount("accounts", "account");
  const { timelinesCreate } = useTimeline("timelines", "timelines");

  const listing = useSelector(
    (state: any) => state[`${listingName}`]["listing"]
  );
  const searchText = useSelector(
    (state: any) => state[`${listingName}`]["searchText"]
  );
  const selected = useSelector(
    (state: any) => state[`${listingName}`]["selected"]
  );
  const isSearching = useSelector(
    (state: any) => state[`${listingName}`]["isSearching"]
  );

  async function fetchAll() {
    const filter: any = { deleted: { eq: "0" } };

    try {
      let nextToken = null;
      let listing: User[] = [];
      let allListing: User[] = [];

      do {
        const response: any = await API.graphql({
          query: listUsers,
          variables: {
            limit: 10000,
            filter,
            nextToken: nextToken,
          },
          authMode: session
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });

        nextToken = response.data.listUsers.nextToken;
        listing = response.data.listUsers.items;

        allListing = [...allListing, ...listing];
      } while (nextToken !== null || nextToken !== undefined);

      return allListing;
    } catch (err) {
      showError(err);

      throw err;
    }
  }

  async function fetch(props: ListingVariables) {
    let { searchText, limit } = props;

    const filter: any = { deleted: { eq: "0" } };
    filter.or = [];
    filter.and = [];

    if (searchText && searchText.length > 0) {
      searchText = searchText.trim().toLowerCase();
      filter.or = [
        { given_name: { contains: searchText } },
        { family_name: { contains: searchText } },
        { nickname: { contains: searchText } },
        { name: { contains: searchText } },
        { username: { contains: searchText } },
        { email: { contains: searchText } },
        { phone_number: { beginsWith: searchText } },
      ];
    }

    if (groupsSelectedFilters && groupsSelectedFilters.length > 0) {
      let or = [];
      for (let filter of groupsSelectedFilters) {
        or.push({ tier: { eq: filter.id } });
      }
      if (or.length > 0) filter.and.push({ or: or });
    }

    if (tiersSelectedFilters && tiersSelectedFilters.length > 0) {
      let or = [];
      for (let filter of tiersSelectedFilters) {
        or.push({ tier: { eq: filter.id } });
      }
      if (or.length > 0) filter.and.push({ or: or });
    }

    if (filter.and && filter.and.length === 0) {
      delete filter.and;
    }
    if (filter.or && filter.or.length === 0) {
      delete filter.or;
    }
    try {
      const response: any = await API.graphql({
        query: listUsers,
        variables: {
          limit: limit,
          filter,
          nextToken: null,
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      let nextToken = response.data.listUsers.nextToken;
      let listing = response.data.listUsers.items;

      dispatch(setListing(listing));
      return listing;
    } catch (err) {
      showError(err);
      return [];
    }
  }

  async function fetchOrdered(props: ListingVariables) {
    let { searchText, limit } = props;

    const filter: any = {};
    filter.or = [];
    filter.and = [];

    if (searchText && searchText.length > 0) {
      searchText = searchText.trim().toLowerCase();
      filter.or = [
        { given_name: { contains: searchText } },
        { family_name: { contains: searchText } },
        { nickname: { contains: searchText } },
        { name: { contains: searchText } },
        { username: { contains: searchText } },
        { email: { contains: searchText } },
        { phone_number: { beginsWith: searchText } },
      ];
    }

    if (groupsSelectedFilters && groupsSelectedFilters.length > 0) {
      let or = [];
      for (let filter of groupsSelectedFilters) {
        or.push({ tier: { eq: filter.id } });
      }
      if (or.length > 0) filter.and.push({ or: or });
    }

    if (tiersSelectedFilters && tiersSelectedFilters.length > 0) {
      let or = [];
      for (let filter of tiersSelectedFilters) {
        or.push({ tier: { eq: filter.id } });
      }
      if (or.length > 0) filter.and.push({ or: or });
    }

    if (filter.and && filter.and.length === 0) {
      delete filter.and;
    }
    if (filter.or && filter.or.length === 0) {
      delete filter.or;
    }

    try {
      const response: any = await API.graphql({
        query: userSortedByOrders,
        variables: {
          deleted: "0",
          filter,
          limit: limit,
          nextToken: null,
          sortDirection: ModelSortDirection.DESC,
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      let nextToken = response.data.userSortedByOrders.nextToken;
      let listing = response.data.userSortedByOrders.items;

      dispatch(setListing(listing));
      return listing;
    } catch (err) {
      showError(err);
      return [];
    }
  }

  async function fetchByEmail(email: string) {
    try {
      const response: any = await API.graphql({
        query: usersByEmail,
        variables: {
          email: email,
          limit: 1,
          nextToken: null,
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      const listing = response.data.usersByEmail.items;
      return listing.length > 0 ? listing[0] : null;
    } catch (err) {
      showError(err);
      return [];
    }
  }

  async function fetchByPhoneNumber(phone_number: string) {
    try {
      const response: any = await API.graphql({
        query: usersByPhoneNumber,
        variables: {
          phone_number: phone_number,
          limit: 1,
          nextToken: null,
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      const listing = response.data.usersByPhoneNumber.items;
      return listing.length > 0 ? listing[0] : null;
    } catch (err) {
      showError(err);
      return [];
    }
  }

  /**
   * Get Resource Name
   *
   * @param id id: string
   *
   * @returns string
   */
  const getName = (id: string) => {
    if (listing.length > 0) {
      const model = listing.find((model: User) => model.id === id);
      return model ? model.name : "";
    } else {
      return "";
    }
  };

  async function get(resourceId: string) {
    try {
      const user: any = await API.graphql({
        query: getUser,
        variables: { id: resourceId },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      return user.data.getUser;
    } catch (err) {
      showError(err);
      return null;
    }
  }

  /**
   * Construct name from given_name, middle_name, and family_name
   *
   * @param given_name given_name: string
   * @param middle_name middle_name: string
   * @param family_name family_name: string
   *
   * @returns string
   */
  const constructName = (
    given_name: string,
    middle_name: string,
    family_name: string
  ) => {
    if (
      given_name.length > 0 &&
      middle_name.length > 0 &&
      family_name.length > 0
    ) {
      return (
        given_name.trim() + " " + middle_name.trim() + " " + family_name.trim()
      );
    }

    if (given_name.length > 0 && family_name.length > 0) {
      return given_name.trim() + " " + family_name.trim();
    }

    if (given_name.length > 0 && middle_name.length > 0) {
      return given_name.trim() + " " + middle_name.trim();
    }

    return given_name;
  };

  async function create(data: any) {
    try {
      // const flags: string[] = extractSelectedCheckboxes("flags_", data);
      // const flagsName: string[] = [];
      // const interests: string[] = extractSelectedCheckboxes("interests_", data);
      // const interestsName: string[] = [];

      if (data.phone_number && !validatePhone(data.phone_number)) {
        throw new Error("Phone number is not valid");
      }

      if (data.phone_number) {
        let userExists = await fetchByPhoneNumber(data.phone_number);
        if (userExists) throw new Error("Phone Number already exists");
      }

      if (data.email) {
        let userExists = await fetchByEmail(data.email);
        if (userExists) throw new Error("Email already exists");
      }

      if (data.birthdate) {
        if (
          data.birthdate <= "1940-01-01" ||
          data.birthdate >= `${new Date().getFullYear() - 10}-01-01`
        ) {
          throw new Error("Invalid Date of Birth");
        }
      }

      const createInput: CreateUserInput = {
        accountID: accountsSelected.id,
        phone_number: data.phone_number,
        email: data.email ? data.email : "",

        given_name: data.given_name ? data.given_name.toLowerCase() : "",
        middle_name: data.middle_name ? data.middle_name.toLowerCase() : "",
        family_name: data.family_name ? data.family_name.toLowerCase() : "",
        nickname: data.nickname ? data.nickname.toLowerCase() : "",
        name: constructName(
          data.given_name ? data.given_name.toLowerCase() : "",
          data.middle_name ? data.middle_name.toLowerCase() : "",
          data.family_name ? data.family_name.toLowerCase() : ""
        ),
        address: data.address ? data.address : "",
        birthdate: data.birthdate ? data.birthdate : "",
        picture: data.picture ? data.picture : "",
        website: data.website ? data.website : "",
        facebook: data.facebook ? data.facebook : "",
        instagram: data.instagram ? data.instagram : "",
        username: data.username ? data.username : "",
        group: data.group
          ? data.group
          : groupsOptions.length > 0
          ? groupsOptions.find(
              (item: any) => item.label === "normal" || item.label === "Normal"
            ).value
          : "",

        stats: [],
        total_points: data.total_points ? Number(data.total_points) : 0,
        points_history: data.points_history ? data.points_history : [],
        tier: data.tier ? data.tier : "",
        tier_name: data.tier ? tiersGetName(data.tier) : "",
        total_spending: 0,
        orders_count: 0,
        deleted: "0",
        createdAt: new Date().toISOString(),
        createdByID: session.sub,
        createdByName: session.name,
      };

      let createdUser: any = await API.graphql<User>({
        query: createUser,
        variables: { input: createInput },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      console.log({
        createdUser,
      });
      createdUser = createdUser.data.createUser;

      dispatch(setListing([createdUser, ...listing]));

      showConfirm(`New ${singleName} has been created successfully`);
      return createdUser;
    } catch (err: Error | any) {
      showError(err.message || err);
      return null;
    }
  }

  async function update(resourceId: string, data: any) {
    const flagsName: string[] = [];
    const interestsName: string[] = [];

    try {
      if (data.phone_number && !validatePhone(data.phone_number)) {
        throw new Error("Phone number is not valid");
      }

      if (data.phone_number) {
        let userExists = await fetchByPhoneNumber(data.phone_number);
        if (userExists) throw new Error("Phone Number already exists");
      }

      if (data.email) {
        let userExists = await fetchByEmail(data.email);
        if (userExists) throw new Error("Email already exists");
      }

      const original = await get(resourceId);
      if (!original) {
        showError("Can not find this user.");
        return;
      }
      let name = original!.name;

      if (data.given_name && data.given_name && data.family_name) {
        name = constructName(
          data.given_name.toLowerCase(),
          "",
          data.family_name.toLowerCase()
        );
      }

      const updateInput: UpdateUserInput = {
        id: original.id,
        conceptID: data.conceptID ? data.conceptID : original!.conceptID,
        phone_number: data.phone_number
          ? data.phone_number
          : original!.phone_number,
        given_name: data.given_name
          ? data.given_name.toLowerCase()
          : original!.given_name,
        middle_name: data.middle_name
          ? data.middle_name.toLowerCase()
          : original!.middle_name,
        nickname: data.nickname
          ? data.nickname.toLowerCase()
          : original!.nickname,
        family_name: data.family_name
          ? data.family_name.toLowerCase()
          : original!.family_name,
        name: name,
        address: data.address ? data.address : original!.address,
        birthdate: data.birthdate ? data.birthdate : original!.birthdate,
        picture: data.picture ? data.picture : original!.picture,
        website: data.website ? data.website : original!.website,
        facebook: data.facebook ? data.facebook : original!.facebook,
        instagram: data.instagram ? data.instagram : original!.instagram,
        username: data.username ? data.username : original!.username,
        group: data.group ? data.group : original!.group,
        email: data.email ? data.email : original!.email,
        stats: data.stats ? data.stats : original!.stats,
        total_points: data.total_points
          ? data.total_points
          : original!.total_points,
        points_history: data.points_history
          ? data.points_history
          : original!.points_history,
        tier: data.tier ? data.tier : original!.tier,
        tier_name: data.tier ? tiersGetName(data.tier) : original!.tier_name,
        points_balance: data.points_balance
          ? data.points_balance
          : original!.points_balance,
        total_spending: data.total_spending
          ? data.total_spending
          : original!.total_spending,
        orders_count: data.orders_count
          ? data.orders_count
          : original!.orders_count,

        createdAt: data.createdAt ? data.createdAt : original!.createdAt,
      };

      let updatedUser: any = await API.graphql<User>({
        query: updateUser,
        variables: { input: updateInput },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });
      console.log({
        updatedUser,
      });

      await timelinesCreate(
        { ...data, customerId: resourceId, name, flagsName, interestsName },
        original!
      );

      setSelectedUser(updatedUser);

      showConfirm(`${singleName} has been updated successfully`);
      return updatedUser;
    } catch (err: Error | any) {
      showError(err.message || err);
      return null;
    }
  }

  /**
   * Fix user creation date
   * Fix total points according to total spending
   * Return to normal tier
   *
   * @returns void
   */
  async function fixUserData() {
    try {
      const users: User[] = await fetchAll();

      if (users) {
        for (let user of users) {
          let points_balance =
            user && user.total_spending ? user.total_spending / 10 : 0;
          const updateUser = {
            total_points: user.total_spending
              ? Math.floor(user.total_spending)
              : 0, // (Rate = 1)
            points_balance: Number(points_balance.toFixed(2)), // (Rate = 10)
            tier: "8a69da49-63d1-48ed-b142-c44266eca42e",
            tier_name: "normal tier",
            createdAt: new Date(user.createdAt).toISOString(),
          };

          const updated = await update(user.id, updateUser);
          // console.log({ updated });
        }
      }
      showConfirm(`Users has been updated successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function updateEmailOnline(resourceId: string, data: any) {
    try {
      if (data.email) {
        const updated = await API.graphql({
          query: updateUser,
          variables: {
            input: {
              id: resourceId,
              email: data.email,
            },
          },
          authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
        });

        setSelectedUser(updated);
      }
    } catch (err: Error | any) {
      showError(err.message || err);
    }
  }

  async function trash(resourceId: string) {
    try {
      const original = await get(resourceId);

      const updated = await API.graphql({
        query: updateUser,
        variables: {
          input: {
            id: original.id,
            deleted: "1",
          },
        },
        authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function bulkTrash(resourceIds: any) {
    for (let resourceId of resourceIds) {
      try {
        await trash(resourceId);
      } catch (err: Error | any) {
        showError(err);
      }
    }

    dispatch(
      setListing(
        listing.filter((resource: any) => !resourceIds.has(resource.id))
      )
    );

    showConfirm(
      `${resourceIds.size} ${listingName} items has been moved to trash`
    );
  }

  async function remove(resourceId: any) {
    try {
      const removedUser = await API.graphql({
        query: deleteUser,
        variables: {
          input: {
            id: resourceId,
          },
        },
        authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      console.log({
        removedUser,
      });

      dispatch(
        setListing(
          listing.filter((resource: any) => resource.id !== resourceId)
        )
      );

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      console.log(err);
      showError(err);
    }
  }

  async function exportAll(params: TransactionListingVariables) {
    try {
      let exportedData = [];

      const data = await fetch(params);

      for (let user of data!) {
        let row: any = { ...user };
        if (user.group) {
          row.groupName = groupsGetName(user.group);
        }
        exportedData.push(row);
      }

      return exportedData;
    } catch (err) {
      showError(err);
    }
  }

  /*
   * Get invoices listed in CSV format
   * Convert them to JSON
   * Save this JSON in local file
   * Loop over this list to submit invoice/credit note
   *
   * */
  async function importDataExcelFile(file: File) {
    try {
      let name: string = "";
      let email: string = "";
      let phoneNumber: string = "";
      let address: string = "";
      let points = 0;
      let newUsers: CreateUserInput[] = [];

      const reader = new FileReader();
      reader.readAsBinaryString(file);
      reader.onload = async (event) => {
        const data = event.target?.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet);

        for (let i = 0; i < rows.length; i++) {
          const row: any = rows[i];

          name = row["name"] || "";
          email = row["email"] || "";
          phoneNumber = String(row["phoneNumber"]) || "";
          address = row["address"] || "";
          points = row["points"] || 0;

          name = name.replace("'", " ").trim();
          email = email.replace("'", " ").trim();
          phoneNumber = phoneNumber.replace("'", " ").trim();
          if (!phoneNumber.startsWith("0")) {
            phoneNumber = "0" + phoneNumber;
          }

          const createInput: any = {
            given_name: name,
            email: email,
            phone_number: phoneNumber,
            total_points: points,
          };

          newUsers.push(createInput);
        }

        // Create users list in parallel
        await Promise.all(newUsers.map((user) => create(user)));
        showConfirm(`Users list has been imported successfully`);

        return newUsers;
      };
    } catch (err: Error | any) {
      console.log(err);
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Name",
    },
    {
      id: "phone_number",
      numeric: false,
      disablePadding: false,
      label: "Mobile",
    },
    {
      id: "tier_name",
      numeric: false,
      disablePadding: false,
      label: "Tier",
    },
    {
      id: "total_points",
      numeric: false,
      disablePadding: false,
      label: "Points",
    },
    {
      id: "total_spending",
      numeric: false,
      disablePadding: false,
      label: "Total Purchase",
    },
    {
      id: "points_balance",
      numeric: false,
      disablePadding: false,
      label: "Available Balance",
    },
    // {
    //   id: "group",
    //   numeric: false,
    //   disablePadding: false,
    //   label: "Group",
    // },
    {
      id: "createdBy",
      numeric: false,
      disablePadding: false,
      label: "Created By",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      label: "",
    },
  ];

  const dataCells: readonly string[] = [
    "name",
    "phone_number",
    "tier_name",
    "total_points",
    "total_spending",
    "points_balance",
    // "group",
  ];

  const options: any[] = [];

  const api: any = {};

  // api[`${listingName}Model`] = User as any;
  api[`${listingName}Selected`] = selected;
  api[`${listingName}IsSearching`] = isSearching;

  api[`${listingName}Listing`] = listing;
  api[`${listingName}Options`] = options;
  api[`${listingName}SearchText`] = searchText;
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;

  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchOrdered`] = fetchOrdered;
  api[`${listingName}FetchAll`] = fetchAll;
  // api[`${listingName}LazyLoad`] = lazyLoad;

  api[`${listingName}Get`] = get;

  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}AddConceptID`] = fixUserData;
  api[`${listingName}UpdateEmailOnline`] = updateEmailOnline;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}BulkTrash`] = bulkTrash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}GetName`] = getName;
  api[`${listingName}Export`] = exportAll;
  api[`${listingName}ImportData`] = importDataExcelFile;

  api[`${listingName}ChangeListing`] = (listing: any[]) =>
    dispatch(setListing(listing));
  api[`${listingName}Search`] = (searchText: string) =>
    dispatch(setSearchText(searchText));
  api[`${listingName}ChangeIsSearching`] = (payload: boolean) =>
    dispatch(setIsSearching(payload));
  api[`${listingName}Select`] = (conceptID: string) =>
    dispatch(setSelected(conceptID));

  return api;
};

export default useResource;
